import { generateRoute } from '../../routing/router.js';
import {
    DASHBOARD_FILTERS_CHANGED_EVENT,
    DASHBOARD_SECTION_STATE_CHANGED_EVENT,
} from './events.js';
import {
    renderError,
    renderSkeletonGrid,
} from './section-renderer.js';
import { parsePaginationFragment } from '../../components/ajax-pagination.js';

export function createDashboardSectionsController({
    sectionsContainer,
    sectionMap,
    restoreFiltersFromUrl = () => false,
}) {
    const sectionStateMap = createSectionStateMap(Array.from(sectionMap.keys()));
    const dashboardEmptyState = sectionsContainer.querySelector('[data-dashboard-empty-state]');
    let activeTagIdsKey = '';
    let activeSortValue = 'playtime';
    let currentActiveStatuses = new Set(sectionMap.keys());

    function initialize() {
        sectionsContainer.addEventListener(DASHBOARD_FILTERS_CHANGED_EVENT, handleFiltersChanged);
        sectionsContainer.addEventListener('pagination:swap', handlePaginationSwap);
        document.addEventListener('pagination:popstate', handlePaginationPopstate);
    }

    function handleFiltersChanged(event) {
        const filterState = resolveFilterState(event, sectionMap);
        const hasTagSelectionChanged = filterState.tagIdsKey !== activeTagIdsKey;
        const hasSortChanged = filterState.sortValue !== activeSortValue;

        if (filterState.resetPages) {
            document.dispatchEvent(new CustomEvent('pagination:invalidate'));
            resetDashboardPageParams(Array.from(sectionMap.keys()));
        }

        activeTagIdsKey = filterState.tagIdsKey;
        activeSortValue = filterState.sortValue;
        currentActiveStatuses = filterState.activeStatuses;

        sectionMap.forEach((section, statusValue) => {
            const state = sectionStateMap.get(statusValue);
            if (!(section instanceof HTMLElement) || state === undefined) {
                return;
            }

            const isStatusEnabled = currentActiveStatuses.has(statusValue);
            const shouldReload = filterState.forceReload
                || hasTagSelectionChanged
                || hasSortChanged
                || state.loadedTagIdsKey !== activeTagIdsKey;

            if (shouldReload) {
                void loadSection(statusValue, section, state, activeTagIdsKey, activeSortValue);
                return;
            }

            applySectionVisibility(section, state, isStatusEnabled);
        });

        syncDashboardEmptyState();
    }

    function handlePaginationSwap(event) {
        const results = event instanceof CustomEvent ? event.detail?.results : null;
        if (
            !(results instanceof HTMLElement)
            || !sectionsContainer.contains(results)
        ) {
            return;
        }

        const section = results.closest('[data-status]');
        const statusValue = String(section?.dataset?.status ?? '').trim();
        const state = sectionStateMap.get(statusValue);
        const totalItems = event.detail?.totalItems;
        if (
            section instanceof HTMLElement
            && state !== undefined
            && Number.isSafeInteger(totalItems)
            && totalItems >= 0
        ) {
            state.isLoading = false;
            state.isError = false;
            state.isEmpty = totalItems === 0;
            state.gameCount = totalItems;
            applySectionVisibility(section, state, currentActiveStatuses.has(statusValue));
            dispatchSectionState(statusValue, state);
        }

        if (event.detail?.adjustViewport !== true) {
            return;
        }

        const heading = section?.querySelector('.dashboard-section-title');
        if (!(section instanceof HTMLElement) || !(heading instanceof HTMLElement)) {
            return;
        }

        if (section.getBoundingClientRect().top < 96) {
            heading.scrollIntoView({
                block: 'start',
                behavior: prefersReducedMotion() ? 'auto' : 'smooth',
            });
        }

        if (event.detail?.keyboardActivated === true) {
            heading.setAttribute('tabindex', '-1');
            heading.focus({ preventScroll: true });
            heading.addEventListener('blur', () => heading.removeAttribute('tabindex'), { once: true });
        }
    }

    function handlePaginationPopstate(event) {
        const publicUrl = event instanceof CustomEvent ? event.detail?.publicUrl : null;
        if (!(publicUrl instanceof URL)) {
            return;
        }

        event.preventDefault();
        document.dispatchEvent(new CustomEvent('pagination:invalidate'));
        if (restoreFiltersFromUrl(publicUrl)) {
            return;
        }

        sectionMap.forEach((section, statusValue) => {
            const state = sectionStateMap.get(statusValue);
            const content = section instanceof HTMLElement
                ? section.querySelector('[data-dashboard-section-content]')
                : null;
            if (!(section instanceof HTMLElement) || !(content instanceof HTMLElement) || state === undefined) {
                return;
            }

            const pageParam = `${statusValue}_page`;
            const currentPage = normalizePositiveInteger(content.dataset.paginationCurrentPage) ?? 1;
            const requestedPage = normalizePositiveInteger(publicUrl.searchParams.get(pageParam)) ?? 1;
            if (currentPage !== requestedPage) {
                void loadSection(statusValue, section, state, activeTagIdsKey, activeSortValue);
            }
        });
    }

    function applySectionVisibility(section, state, isStatusEnabled) {
        if (!isStatusEnabled) {
            section.hidden = true;
            syncDashboardEmptyState();
            return;
        }

        if (state.isLoading) {
            section.hidden = false;
            syncDashboardEmptyState();
            return;
        }

        if (state.isEmpty) {
            section.hidden = true;
            syncDashboardEmptyState();
            return;
        }

        section.hidden = false;
        syncDashboardEmptyState();
    }

    async function loadSection(statusValue, section, state, tagIdsKey, sortValue) {
        const sectionContent = section.querySelector('[data-dashboard-section-content]');
        if (!(sectionContent instanceof HTMLElement) || sectionContent.id === '') {
            return;
        }

        state.requestId += 1;
        const requestId = state.requestId;
        state.isLoading = true;
        state.isError = false;
        state.isEmpty = false;
        state.gameCount = 0;

        sectionContent.innerHTML = renderSkeletonGrid();
        sectionContent.setAttribute('aria-busy', 'true');
        applySectionVisibility(section, state, currentActiveStatuses.has(statusValue));
        dispatchSectionState(statusValue, state);

        const sectionRoute = buildSectionRoute(statusValue, tagIdsKey, sortValue);
        const fragment = sectionRoute === null
            ? null
            : await fetchSection(sectionRoute, sectionContent.id);
        if (requestId !== state.requestId) {
            return;
        }

        state.isLoading = false;
        state.loadedTagIdsKey = tagIdsKey;

        if (fragment === null) {
            state.isError = true;
            state.isEmpty = false;
            state.gameCount = 0;
            renderError(sectionContent);
            applySectionVisibility(section, state, currentActiveStatuses.has(statusValue));
            dispatchSectionState(statusValue, state);
            return;
        }

        state.isError = false;
        state.gameCount = fragment.totalItems;
        state.isEmpty = state.gameCount === 0;

        applySectionFragment(sectionContent, fragment);
        syncCanonicalSectionPage(fragment.pageParam, fragment.currentPage);
        revealCards(sectionContent);
        applySectionVisibility(section, state, currentActiveStatuses.has(statusValue));
        dispatchSectionState(statusValue, state);
    }

    function dispatchSectionState(statusValue, state) {
        sectionsContainer.dispatchEvent(new CustomEvent(DASHBOARD_SECTION_STATE_CHANGED_EVENT, {
            detail: {
                statusValue,
                isLoading: state.isLoading,
                isEmpty: state.isEmpty,
            },
        }));
    }

    function syncDashboardEmptyState() {
        if (!(dashboardEmptyState instanceof HTMLElement)) {
            return;
        }

        if (currentActiveStatuses.size === 0) {
            setDashboardEmptyStateVisibility(dashboardEmptyState, true);
            return;
        }

        const activeStates = Array.from(sectionStateMap.entries())
            .filter(([statusValue]) => currentActiveStatuses.has(statusValue))
            .map(([, state]) => state);

        if (activeStates.some((state) => state.isLoading || state.isError)) {
            setDashboardEmptyStateVisibility(dashboardEmptyState, false);
            return;
        }

        const totalVisibleGames = activeStates.reduce((total, state) => total + state.gameCount, 0);
        setDashboardEmptyStateVisibility(dashboardEmptyState, totalVisibleGames === 0);
    }

    return {
        initialize,
    };
}

function buildSectionRoute(statusValue, tagIdsKey, sortValue) {
    const route = generateRoute('app_api_dashboard_section', { status: statusValue });
    if (typeof route !== 'string') {
        return null;
    }

    const url = new URL(route, window.location.origin);
    url.search = window.location.search;

    if (tagIdsKey !== '') {
        url.searchParams.set('tag_ids', tagIdsKey.split('|').join(','));
    } else {
        url.searchParams.delete('tag_ids');
    }

    if (typeof sortValue === 'string' && sortValue.trim() !== '' && sortValue.trim() !== 'playtime') {
        url.searchParams.set('sort', sortValue.trim());
    } else {
        url.searchParams.delete('sort');
    }

    return url.toString();
}

async function fetchSection(route, targetId) {
    try {
        const response = await fetch(route, {
            method: 'GET',
            headers: {
                Accept: 'text/html',
                'X-Requested-With': 'XMLHttpRequest',
            },
        });

        if (!response.ok) {
            return null;
        }

        const fragment = parsePaginationFragment(
            document,
            await response.text(),
            `#${CSS.escape(targetId)}`,
        );

        return fragment.totalItems === null
            || fragment.currentPage === null
            || fragment.totalPages === null
            || fragment.pageParam === ''
            || fragment.pageLabel === ''
            ? null
            : fragment;
    } catch (error) {
        console.error('Dashboard section request failed', error);
        return null;
    }
}

function applySectionFragment(sectionContent, fragment) {
    sectionContent.innerHTML = fragment.markup;
    sectionContent.dataset.paginationTotalItems = String(fragment.totalItems);
    sectionContent.dataset.paginationCurrentPage = String(fragment.currentPage);
    sectionContent.dataset.paginationTotalPages = String(fragment.totalPages);
    sectionContent.dataset.paginationPageParam = fragment.pageParam;
    sectionContent.dataset.paginationPageLabel = fragment.pageLabel;
    sectionContent.setAttribute('aria-busy', 'false');
}

function revealCards(sectionContent) {
    sectionContent.querySelectorAll('[data-dashboard-game-card]').forEach((card) => {
        if (card instanceof HTMLElement) {
            card.classList.add('ui-game-card-reveal');
        }
    });
}

function resetDashboardPageParams(statusValues) {
    const url = new URL(window.location.href);
    statusValues.forEach((statusValue) => url.searchParams.delete(`${statusValue}_page`));
    window.history.replaceState(window.history.state ?? null, '', `${url.pathname}${url.search}${url.hash}`);
}

function syncCanonicalSectionPage(pageParam, page) {
    const url = new URL(window.location.href);
    if (page > 1) {
        url.searchParams.set(pageParam, String(page));
    } else {
        url.searchParams.delete(pageParam);
    }

    window.history.replaceState(window.history.state ?? null, '', `${url.pathname}${url.search}${url.hash}`);
}

function normalizePositiveInteger(value) {
    const normalized = String(value ?? '').trim();

    return /^[1-9]\d*$/.test(normalized) ? Number.parseInt(normalized, 10) : null;
}

function createSectionStateMap(statusValues) {
    const map = new Map();

    statusValues.forEach((statusValue) => {
        map.set(statusValue, {
            loadedTagIdsKey: null,
            isLoading: false,
            isError: false,
            isEmpty: false,
            gameCount: 0,
            requestId: 0,
        });
    });

    return map;
}

function setDashboardEmptyStateVisibility(dashboardEmptyState, isVisible) {
    dashboardEmptyState.hidden = isVisible === false;
    dashboardEmptyState.style.display = isVisible ? '' : 'none';
}

function resolveFilterState(event, sectionMap) {
    const detail = event instanceof CustomEvent && isObject(event.detail)
        ? event.detail
        : {};

    return {
        activeStatuses: normalizeStatusValues(detail.activeStatuses, sectionMap),
        tagIdsKey: typeof detail.tagIdsKey === 'string' ? detail.tagIdsKey : '',
        sortValue: typeof detail.sortValue === 'string' && detail.sortValue.trim() !== ''
            ? detail.sortValue.trim()
            : 'playtime',
        forceReload: detail.forceReload === true,
        resetPages: detail.resetPages === true,
    };
}

function normalizeStatusValues(rawStatuses, sectionMap) {
    const normalized = new Set();

    if (Array.isArray(rawStatuses)) {
        rawStatuses.forEach((rawStatus) => {
            if (typeof rawStatus !== 'string') {
                return;
            }

            const statusValue = rawStatus.trim();
            if (statusValue !== '' && sectionMap.has(statusValue)) {
                normalized.add(statusValue);
            }
        });
    }

    if (normalized.size === 0 && Array.isArray(rawStatuses) === false) {
        sectionMap.forEach((_, statusValue) => normalized.add(statusValue));
    }

    return normalized;
}

function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function isObject(value) {
    return typeof value === 'object' && value !== null;
}
